// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Operators scan these cards for job types that failed during the
 * selected month, then use the recent-jobs table for details. Render only job
 * types present in the recorded summary and emphasize every non-zero error
 * count; hiding or muting those counts would make an unhealthy job type appear
 * equivalent to one that completed successfully.
 */
import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui'
import { useMonthlyStats } from '../../hooks/useJobs'
import { formatJobType } from '../../utils/formatJobType'

interface StatsViewProps {
  month: string
  org_id?: string
  ws_id?: string
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error
    && typeof (error as Record<string, unknown>).message === 'string') {
    return (error as { message: string }).message
  }
  return 'Unknown error'
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-[550] text-slate-500">{label}</dt>
      <dd className={`mt-0.5 text-[17px] font-[650] tracking-[-0.01em] tabular-nums ${alert ? 'text-[#dc2626]' : 'text-slate-900'}`}>
        {value}
      </dd>
    </div>
  )
}

export function MonthlyStatsView({ month, org_id, ws_id }: StatsViewProps) {
  const { data: stats, isLoading, isError, error } = useMonthlyStats(month, org_id, ws_id)

  if (isError) {
    console.error('Error fetching monthly stats:', error)
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Error loading stats: {getErrorMessage(error)}
        </AlertDescription>
      </Alert>
    )
  }

  if (isLoading || !stats) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-10 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-[13px] text-slate-500">Loading stats…</span>
      </div>
    )
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {Object.entries(stats).map(([type, data]) => (
        <div
          key={type}
          className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
        >
          <div className="text-sm font-semibold tracking-[-0.01em]">{formatJobType(type)}</div>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3.5">
            <Stat label="Success rate" value={`${data.success_rate.toFixed(1)}%`} />
            <Stat label="Avg wait time" value={`${(data.avg_wait_duration_ms / 1000).toFixed(1)}s`} />
            <Stat label="Avg run time" value={`${(data.avg_run_duration_ms / 1000).toFixed(1)}s`} />
            <Stat label="Completed" value={`${data.completed_jobs}/${data.total_jobs}`} />
            <Stat label="Errors" value={String(data.error_count)} alert={data.error_count > 0} />
          </dl>
        </div>
      ))}
    </div>
  )
}
